/**
 * TI-407 — at-least-once outbox for `POST /livekit/record/timeline/client`.
 *
 * The recording timeline reporter (useRecordingTimelineReporter) used to POST each
 * media-state transition fire-and-forget: any event emitted before the telemetry JWT
 * arrived was dropped, and any network failure was swallowed with no retry. On slow
 * networks this produced incomplete audit timelines (AC4: "emit complete events, no
 * silent drops on slow networks").
 *
 * This outbox fixes both:
 *  - enqueue() buffers every event body (browser fields already merged in by the caller).
 *  - flush() drains the queue via keepalive fetch with capped exponential-backoff retry
 *    on network failure / retryable non-2xx. It BUFFERS (never drops) while no telemetry
 *    JWT is available yet, and is re-triggered by the reporter once the JWT arrives.
 *  - flushBeacon() best-effort ships whatever remains on page unload via
 *    navigator.sendBeacon (+ a keepalive-fetch backstop that carries the Bearer header).
 *
 * The VSS endpoint already dedupes (recording:tlDedupe:* keys), so at-least-once /
 * double-send is safe.
 *
 * NOTE (cross-repo): navigator.sendBeacon cannot set an Authorization header, so the JWT
 * is carried in the request BODY as `telemetryJwt`. VSS `checkTelemetryJwt`
 * (video-streaming-server/livekit/recordingTimelineClient.js) currently reads the JWT
 * ONLY from the `Authorization` header — the beacon path will 403 there until VSS also
 * accepts `body.telemetryJwt`. The keepalive-fetch backstop below carries the Bearer
 * header and delivers today; the beacon becomes reliable once VSS is updated.
 */
import { ENDPOINTS } from "./endpoints";
import { captureException, reportClientLog } from "./clientLogReporter";

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 10000;
const DEFAULT_MAX_QUEUE = 500;

function isRetryableStatus(status) {
  // Network error (0) / timeout (408) / rate-limit (429) / server (5xx) → retry.
  // Also 403: the telemetry JWT may just be refreshing/expiring mid-session.
  // Other 4xx (400 malformed event, 404, …) are permanent → drop after logging.
  return status === 0 || status === 403 || status === 408 || status === 429 || status >= 500;
}

/**
 * @param {object} opts
 * @param {() => (string|null|undefined)} opts.getJwt   returns the current telemetry JWT (or falsy).
 * @param {string} [opts.endpoint]        POST target (defaults to ENDPOINTS.RECORD_TIMELINE_CLIENT).
 * @param {number} [opts.maxAttempts]     cap on retry attempts per event.
 * @param {number} [opts.baseDelayMs]     backoff base (attempt N delay = base * 2^(N-1), capped).
 * @param {number} [opts.maxDelayMs]      backoff cap.
 * @param {number} [opts.maxQueue]        drop oldest beyond this many buffered events.
 * @param {typeof fetch}   [opts.fetchImpl] injectable fetch (tests).
 * @param {(ms:number)=>Promise<void>} [opts.sleepImpl] injectable delay (tests).
 */
export function createTimelineOutbox({
  getJwt,
  endpoint = ENDPOINTS.RECORD_TIMELINE_CLIENT,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  baseDelayMs = DEFAULT_BASE_DELAY_MS,
  maxDelayMs = DEFAULT_MAX_DELAY_MS,
  maxQueue = DEFAULT_MAX_QUEUE,
  fetchImpl,
  sleepImpl,
} = {}) {
  const queue = [];
  let drainPromise = null;

  const doFetch = (...args) => (fetchImpl || (typeof fetch !== "undefined" ? fetch : undefined))?.(...args);
  const sleep = sleepImpl || ((ms) => new Promise((r) => setTimeout(r, ms)));
  const jwtOf = () => (typeof getJwt === "function" ? getJwt() : null);

  function backoff(attempt) {
    return Math.min(baseDelayMs * 2 ** (attempt - 1), maxDelayMs);
  }

  /** @returns {'ok' | 'retry' | 'drop'} */
  async function sendOnce(jwt, body) {
    try {
      const res = await doFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
        body: JSON.stringify(body),
        keepalive: true,
      });
      if (res && res.ok) return "ok";
      const status = res ? res.status : 0;
      const text = res && typeof res.text === "function" ? await res.text().catch(() => "") : "";
      reportClientLog("timeline_ingest_http", `${status} ${text}`.slice(0, 480));
      return isRetryableStatus(status) ? "retry" : "drop";
    } catch (err) {
      captureException(err, "timeline_network");
      return "retry";
    }
  }

  async function drain() {
    // Buffer until a JWT exists — flush() is re-invoked by the reporter when it arrives.
    if (!jwtOf()) return;
    while (queue.length) {
      const jwt = jwtOf();
      if (!jwt) return; // JWT went away mid-drain — keep the rest buffered.
      const item = queue[0];
      const result = await sendOnce(jwt, item.body);
      // Guard against a concurrent flushBeacon() that spliced the queue while we awaited.
      if (queue[0] !== item) continue;
      if (result === "ok" || result === "drop") {
        if (result === "drop") {
          reportClientLog(
            "timeline_outbox_drop",
            `dropped non-retryable timeline event: ${item.body?.event || "?"}`
          );
        }
        queue.shift();
      } else {
        item.attempts += 1;
        if (item.attempts >= maxAttempts) {
          queue.shift();
          reportClientLog(
            "timeline_outbox_giveup",
            `dropped timeline event after ${maxAttempts} attempts: ${item.body?.event || "?"}`
          );
        } else {
          await sleep(backoff(item.attempts));
        }
      }
    }
  }

  /** Kick the drain loop. Concurrent callers share the in-flight promise. Awaitable for tests. */
  function flush() {
    if (!drainPromise) {
      drainPromise = drain().finally(() => {
        drainPromise = null;
      });
    }
    return drainPromise;
  }

  function enqueue(body) {
    if (!body) return;
    queue.push({ body, attempts: 0 });
    if (queue.length > maxQueue) {
      const dropped = queue.length - maxQueue;
      queue.splice(0, dropped); // drop oldest under sustained backpressure — surfaced, not silent.
      reportClientLog(
        "timeline_outbox_overflow",
        `dropped ${dropped} oldest timeline event(s) (queue > ${maxQueue})`
      );
    }
    flush();
  }

  function beaconSend(item, jwt) {
    let beaconOk = false;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // JWT in the body — sendBeacon cannot set Authorization (see module note / VSS follow-up).
      const payload = jwt ? { ...item.body, telemetryJwt: jwt } : item.body;
      try {
        const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
        beaconOk = navigator.sendBeacon(endpoint, blob);
      } catch (_e) {
        beaconOk = false;
      }
    }
    // Backstop that carries the Bearer header (VSS auth reads the header today). keepalive lets
    // it survive unload; fire-and-forget — no retry on the way out. Double-send is dedup-safe.
    if (jwt) {
      try {
        doFetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
          body: JSON.stringify(item.body),
          keepalive: true,
        })?.catch?.(() => {});
      } catch (_e) {
        /* best-effort on unload */
      }
    }
    return beaconOk;
  }

  /**
   * Best-effort flush on page unload. Drains the whole queue via sendBeacon (+ keepalive
   * fetch backstop). Synchronous by design — the event loop may not run async work post-unload.
   * @returns {number} count the browser accepted for beacon delivery.
   */
  function flushBeacon() {
    const jwt = jwtOf();
    const items = queue.splice(0, queue.length);
    let sent = 0;
    for (const item of items) {
      if (beaconSend(item, jwt)) sent += 1;
    }
    return sent;
  }

  return {
    enqueue,
    flush,
    flushBeacon,
    /** @returns {number} buffered (not-yet-delivered) event count — for tests/diagnostics. */
    size: () => queue.length,
  };
}
